import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  isVoiceCallEnabled,
  getBridgeInboxType,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useBuzzerBridgeSip } from 'dashboard/composables/useBuzzerBridgeSip';
import { BUZZER_BRIDGE_REGISTRATION_STATUS } from 'dashboard/services/BuzzerBridgeSipClient';
import BuzzerBridgeCallsAPI from 'dashboard/api/channel/voice/buzzerBridgeCallsAPI';

const SIP_LANGS = ['he', 'en', 'ru'];

// The one outbound path through Buzzer Bridge: create the call server-side, register it in the
// calls store, send the SIP INVITE, then open the conversation. Used by the contact call button
// and the free dialler.
export function useBridgeOutboundCall() {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const { t } = useI18n();
  const bridgeSession = useBuzzerBridgeSip();

  const inboxesList = useMapGetter('inboxes/getInboxes');
  const currentUser = useMapGetter('getCurrentUser');
  const isInitiatingCall = ref(false);

  // Calls go through Buzzer Bridge, so an inbox is only dialable by agents
  // provisioned with SIP credentials.
  const canUseInbox = inbox =>
    isVoiceCallEnabled(inbox) && !!currentUser.value?.voice_calls_enabled;

  const voiceInboxes = computed(() =>
    (inboxesList.value || []).filter(canUseInbox)
  );

  // Block a new call whenever any provider call is already active or ringing,
  // otherwise starting one here would leave a still-live session with no control.
  const isCallDisabled = computed(
    () =>
      store.getters['calls/hasActiveCall'] ||
      store.getters['calls/hasIncomingCall'] ||
      isInitiatingCall.value
  );

  const navigateToConversation = conversationId => {
    const accountId = route.params.accountId;
    if (conversationId && accountId) {
      router.push({
        path: frontendURL(conversationUrl({ accountId, id: conversationId })),
      });
    }
  };

  // `phone` is the E.164 destination the browser dials. The server resolves the contact from
  // `conversationId` or `contactId`; without either it is a free dial and the number itself is sent.
  const startCall = async ({
    inbox,
    phone,
    contactId = null,
    conversationId = null,
  }) => {
    if (isCallDisabled.value) return false;
    if (
      bridgeSession.registrationStatus.value !==
      BUZZER_BRIDGE_REGISTRATION_STATUS.REGISTERED
    ) {
      useAlert(t('CONTACT_PANEL.BRIDGE_NOT_CONNECTED'));
      return false;
    }

    const clientRef = window.crypto.randomUUID();
    let call;
    isInitiatingCall.value = true;
    try {
      call = await BuzzerBridgeCallsAPI.initiate({
        conversationId,
        contactId,
        phoneNumber: contactId || conversationId ? null : phone,
        inboxId: inbox.id,
        clientRef,
      });
      store.dispatch('calls/addCall', {
        callSid: call.call_sid,
        callId: call.id,
        conversationId: call.conversation_id,
        inboxId: call.inbox_id,
        callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
        provider: VOICE_CALL_PROVIDERS.BRIDGE,
        caller: call.caller,
      });

      const locale = currentUser.value?.ui_settings?.locale;
      await bridgeSession.dial(phone, {
        account_id: Number(route.params.accountId),
        inbox_id: inbox.id,
        inbox_type: getBridgeInboxType(inbox),
        conversation_id: call.conversation_id,
        agent_email: currentUser.value?.email,
        lang: SIP_LANGS.includes(locale) ? locale : undefined,
        client_ref: clientRef,
      });
      navigateToConversation(call.conversation_id);
      useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
      return true;
    } catch (error) {
      if (call?.id) {
        BuzzerBridgeCallsAPI.report(call.id, {
          status: 'failed',
          endReason: 'failed',
        }).catch(() => {});
        store.dispatch('calls/dismissCall', call.call_sid);
      }
      const message = parseAPIErrorResponse(error);
      useAlert(
        typeof message === 'string'
          ? message
          : error?.message || t('CONTACT_PANEL.CALL_FAILED')
      );
      return false;
    } finally {
      isInitiatingCall.value = false;
    }
  };

  return {
    canUseInbox,
    voiceInboxes,
    isCallDisabled,
    isInitiatingCall,
    startCall,
  };
}
